import { useState } from "react";

import { useFileRecordApi } from "../../../../../common/api/master-api/file-records/useFileRecordApi";
import { mapToNoteItem } from "../../../../../common/api/master-api/notes/GetNotesBySubjectIdsResponse";
import { useNotesApi } from "../../../../../common/api/master-api/notes/useNotesApi";
import { NoteItem } from "../../../../../common/components/notes/hooks/state/NoteItem";
import { FileRecordStatus } from "../../../../../common/types/FileRecordStatus";
import { mapToFileRecordListingItem } from "../../../../file-records/listing/types/FileRecordListingItem";
import { FileRecordLookupItem } from "../FileRecordLookupItem";
import { applicableFileRecordLookupStatuses } from "../utils/applicableFileRecordLookupStatuses";
import { FileRecordLookupListingFilters } from "./useFileRecordLookupListingFilters";

export const useFileRecordLookupListing = () => {
    const { getFileRecordsByQuery } = useFileRecordApi();
    const { getNotesBySubjectIds } = useNotesApi();
    const [notes, setNotes] = useState<Record<string, NoteItem[]>>();
    const [items, setItems] = useState<FileRecordLookupItem[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [areNotesLoading, setAreNotesLoading] = useState<boolean>(true);
    const [areFileRecordsLoading, setAreFileRecordsLoading] = useState<boolean>(false);

    const reloadData = async (filters: FileRecordLookupListingFilters): Promise<void> => {
        setAreFileRecordsLoading(true);

        const fileRecordListResponse = await getFileRecordsByQuery({
            page: filters.page,
            pageSize: filters.pageSize,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder,
            filePath: filters.filePath,
            content: filters.content,
            deliveryRef: filters.deliveryRef,
            deliveryFileName: filters.deliveryFileName,
            statuses: getStatuses(filters.statuses),
            recordTypes: filters.recordTypes,
            associations: filters.associations,
            createdDateFrom: filters.createdDateFrom?.toISOString(),
            createdDateTo: filters.createdDateTo?.toISOString()
        });

        const mappedItems = fileRecordListResponse.items.map(mapToFileRecordListingItem);

        setItems(mappedItems);
        setTotalItems(fileRecordListResponse.totalCount);

        setAreFileRecordsLoading(false);

        await reloadNotes(mappedItems);
    };

    const reloadNotes = async (mappedItems: FileRecordLookupItem[]) => {
        setAreNotesLoading(true);

        const subjectIds = mappedItems.map((i) => i.id);

        const notesResponse = await getNotesBySubjectIds({
            subjectIds
        });

        const mappedNotes = Object.entries(notesResponse).reduce((acc, [key, value]) => {
            acc[key] = value.map((item) => mapToNoteItem(item, key));

            return acc;
        }, {} as Record<string, NoteItem[]>);

        setNotes(mappedNotes);
        setAreNotesLoading(false);
    };

    return {
        items,
        notes,
        totalItems,
        reloadData,
        isLoading: areFileRecordsLoading,
        areNotesLoading
    };
};

const getStatuses = (statuses?: FileRecordStatus[]): FileRecordStatus[] => {
    if (!statuses?.length) {
        return applicableFileRecordLookupStatuses;
    }

    return statuses;
};
